import { create } from 'zustand';
import { api, configureApi, setTenantSlug, resolveTenantSlug } from './api';

export type Membership = {
  tenantId: string;
  role: 'admin' | 'user';
  slug: string | null;
  name: string | null;
  status: string | null;
};

export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'global_superadmin' | 'tenant_admin' | 'tenant_user';
  status: string;
  tenants?: Membership[];
};

// Pick the active tenant slug for a freshly authenticated user. Prefer
// whatever is already in localStorage / URL / subdomain (so manual switches
// stick), then fall back to the first membership returned by the server.
function pickTenantSlug(user: User | null): string | null {
  const existing = resolveTenantSlug();
  if (existing) return existing;
  const first = user?.tenants?.find((t) => !!t.slug);
  return first?.slug ?? null;
}

type AuthState = {
  user: User | null;
  accessToken: string | null;
  hydrating: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  hydrate: () => Promise<void>;
};

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  hydrating: true,

  async login(email, password) {
    const { data } = await api.post('/api/auth/login', { email, password });
    const slug = pickTenantSlug(data.user);
    if (slug) setTenantSlug(slug);
    set({ accessToken: data.accessToken, user: data.user });
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } catch {
      /* ignore */
    }
    setTenantSlug(null);
    set({ accessToken: null, user: null });
  },

  async refreshAccessToken() {
    try {
      const { data } = await api.post('/api/auth/refresh');
      set({ accessToken: data.accessToken });
      return data.accessToken as string;
    } catch {
      set({ accessToken: null, user: null });
      return null;
    }
  },

  async hydrate() {
    set({ hydrating: true });
    const tok = await get().refreshAccessToken();
    if (tok) {
      try {
        const { data } = await api.get('/api/auth/me');
        const slug = pickTenantSlug(data.user);
        if (slug) setTenantSlug(slug);
        set({ user: data.user });
      } catch {
        set({ user: null, accessToken: null });
      }
    }
    set({ hydrating: false });
  },
}));

configureApi({
  getAccessToken: () => useAuth.getState().accessToken,
  refresh: () => useAuth.getState().refreshAccessToken(),
  onAuthFail: () => useAuth.setState({ accessToken: null, user: null }),
});
